/**
 * Warm start message mappings for different business scenarios
 * These messages are used when users arrive via specific links with topic parameters
 * URL: /guide/company/${businessEntityUuid}?topic=article-selection
 * URL: /guide/company/${businessEntityUuid}?topic=meeting-minutes-loan
 * URL: /guide/company/${businessEntityUuid}?topic=meeting-minutes-investors
 * URL: /guide/company/${businessEntityUuid}?topic=meeting-minutes-disputes
 * URL: /guide/company/${businessEntityUuid}?topic=meeting-minutes-banking
 */

export const WARM_START_MESSAGES = {
  '5-things-business-taxes': {
    agentName: 'article-selection',
    message: '5 things to know about the difference between personal and business taxes',
    messageType: 'invoke'
  },
  '5-things-licensing-permit-requirements': {
    agentName: 'article-selection',
    message: '5 things to know about licensing and permit requirements',
    messageType: 'invoke'
  },
  'april-taxes-next-up': 'Tell me about April Tax Services',
  // 'banking': 'Hello! How do I set up business banking for my company?',
  'best-way-to-bill': 'What\'s the best way to bill customers and partners?',
  // 'business-plan': 'Hi! I need help creating a comprehensive business plan for my company.',
  'build-customer-trust': 'How can I build trust with customers?',
  'business-cards-materials': 'Do I need business cards or other materials for my business?',
  'business-cost': {
    agentName: 'business-cost-estimator-form',
    message: 'Hello! I want to get business cost estimates for my business.',
    messageType: 'invoke'
  },
  'business-idea': {
    agentName: 'business-idea-generation-form',
    message: 'Hello! I want to get business ideas for my business.',
    messageType: 'invoke'
  },
  'business-logo': 'Why does every business need a logo?',
  'business-name': {
    agentName: 'business-name-generation-form',
    message: 'Hello! I need help generating business names for my business.',
    messageType: 'invoke'
  },
  'business-name-research': {
    agentName: 'business-name-research-form',
    message: 'Hello! I have a business name but I need to research its availability.',
    messageType: 'invoke'
  },
  'market-analysis': {
    agentName: 'market-analysis-form',
    message: 'Hello! I want to analyze my market.',
    messageType: 'invoke'
  },
  'businesses-track-expenses': {
    agentName: 'article-selection',
    message: 'Why should new businesses track expenses?',
    messageType: 'invoke'
  },
  // 'compliance': 'Hello! Can you explain business compliance requirements I should know about?',
  // 'default': 'Hello! I could use some business guidance.',
  'expense-tracking-business-trips': 'How do I know which trips qualify as business-related?',
  'expense-tracking-taxes': 'How does expense tracking help me with taxes?',
  // 'funding': 'Hi there! What are the best ways to fund a new business?',
  // 'legal-structure': 'Hi! What business structure should I choose for my company?',
  // 'licensing': 'Hello! What licenses do I need for my business?',
  'log-business-miles': 'How do I log miles for business trips?',
  'logo-placement': 'Where should I be using my logo?',
  // 'marketing': 'Hello! I need advice on marketing strategies for my business.',
  'meeting-minutes-banking': 'Do I need my meeting minutes to open a bank account?',
  'meeting-minutes-disputes': 'How can meeting minutes help me when responding to disputes, IRS audits, or legal claims?',
  'meeting-minutes-funding': 'How can having regular detailed meeting minutes help me to get funding?',
  'meeting-minutes-investors': 'Do investors need my company\'s meeting minutes?',
  'meeting-minutes-loan': 'Why do I need to have meeting minutes for my loan application?',
  'no-logo-risks': 'What are the risks of not having a consistent, professional logo?',
  // 'taxes': 'Hi! Can you help me understand business tax obligations?',
  'questions-about-compliance': 'I have questions about compliance',
  'questions-about-licenses': 'I have questions about licenses and permits',
  'questions-about-taxes': 'I have questions about taxes',
  'separate-personal-business-banking': {
    agentName: 'article-selection',
    message: 'Why separate personal and business banking?',
    messageType: 'invoke'
  },
  'social-presence-create': 'How do I create a social presence?',
  'track-business-miles': 'Should every business track miles?',
  'track-business-trip-expenses': 'What expenses do I need to track on business trips?',
  'trustworthy-website': 'What makes a website trustworthy to customers?',
  'website-pages': 'What pages should every business website include?',
  'article-selection': {
    agentName: 'article-selection',
    message: 'Hello! I need help selecting an article for my business.',
    messageType: 'invoke'
  },
  'investors-meeting-minutes': 'Do investors need my company\'s meeting minutes?',
  'business-trip-receipts': 'How long do I need to keep receipts from business trips?',
};

/**
 * Gets the appropriate warm start message configuration for a given topic code
 * @param {string} code - The topic code from URL parameters
 * @returns {string|object} The corresponding warm start message or configuration object
 */
export const getWarmStartMessage = (code) => {
  return WARM_START_MESSAGES[code] || WARM_START_MESSAGES.default;
};

/**
 * Gets the message configuration object for thread creation
 * @param {string} code - The topic code from URL parameters
 * @param {string} businessEntityUuid - The business entity UUID
 * @returns {object} Configuration object for thread creation
 */
export const getWarmStartConfig = (code, businessEntityUuid = undefined) => {
  const config = getWarmStartMessage(code);

  // Handle object configuration (invoke messageType)
  if (typeof config === 'object' && config !== null) {
    return {
      ...(businessEntityUuid && { businessEntityUuid }),
      messageType: config.messageType,
      message: config.message,
      role: 'user',
      visible: false,
      ...(config.agentName && { agentName: config.agentName })
    };
  }

  // Handle string configuration (text messageType)
  return {
    businessEntityUuid,
    messageType: 'text',
    message: config,
    role: 'user',
    visible: false
  };
};

/**
 * Validates if a topic code is supported
 * @param {string} code - The topic code to validate
 * @returns {boolean} True if the code is valid
 */
export const isValidWarmStartCode = (code) => {
  return Boolean(code && code !== 'default' && Object.prototype.hasOwnProperty.call(WARM_START_MESSAGES, code));
};

/**
 * Gets all available topic codes
 * @returns {string[]} Array of all available topic codes
 */
export const getAvailableWarmStartCodes = () => {
  return Object.keys(WARM_START_MESSAGES).filter(code => code !== 'default');
}; 